<template>
  <div class="noPrint floating-chat-bot-v2">
    <!-- Draggable Chat Bot Button -->
    <div
      ref="chatBotButton"
      class="floating-chat-bot-button"
      :class="{ dragging: isDragging }"
      :style="buttonStyles"
      @mousedown="startDrag"
      @touchstart="startDrag"
      @click="onChatBotClickedHandler"
      v-if="positionLoaded && !showChatBot && isAllowed"
    >
      <Orb :size="isMobile ? 75 : 90" :colors="orbColors" />
    </div>

    <!-- Chat Bot Popover -->
    <div
      ref="chatBotPopover"
      class="chat-bot-popover"
      :class="{
        show: showChatBot,
        fullscreen: isFullscreen,
        'exiting-fullscreen': isExitingFullscreen,
        dragging: isPopoverDragging,
        resizing: isResizing,
        'custom-sized': !!customSize || isResetting,
        docked: isDocked,
      }"
      :style="[popoverStyles, themeVars]"
      v-if="isAllowed"
    >
      <oliver-chat-panel
        ref="OLIVER_CHAT_PANEL"
        :is-fullscreen="isFullscreen"
        :is-customized="isCustomized"
        :is-wide-layout="isWideLayout"
        :is-grid-layout="isGridLayout"
        :is-docked="isDocked"
        :is-open="showChatBot"
        @toggle-fullscreen="toggleFullscreen"
        @reset-size="resetSize"
        @close="onChatBotClickedHandler"
        @toggle-dock="toggleDock"
        @start-popover-drag="startPopoverDrag"
      />

      <!-- Resize handles (hidden in fullscreen, docked, and on mobile) -->
      <template v-if="!isFullscreen && !isDocked && deviceType !== 'mobile'">
        <div
          class="resize-handle resize-handle--e"
          @mousedown.prevent.stop="startResize('e', $event)"
          @touchstart.prevent.stop="startResize('e', $event)"
        ></div>
        <div
          class="resize-handle resize-handle--w"
          @mousedown.prevent.stop="startResize('w', $event)"
          @touchstart.prevent.stop="startResize('w', $event)"
        ></div>
        <div
          class="resize-handle resize-handle--n"
          @mousedown.prevent.stop="startResize('n', $event)"
          @touchstart.prevent.stop="startResize('n', $event)"
        ></div>
        <div
          class="resize-handle resize-handle--s"
          @mousedown.prevent.stop="startResize('s', $event)"
          @touchstart.prevent.stop="startResize('s', $event)"
        ></div>
        <div
          class="resize-handle resize-handle--ne"
          @mousedown.prevent.stop="startResize('ne', $event)"
          @touchstart.prevent.stop="startResize('ne', $event)"
        ></div>
        <div
          class="resize-handle resize-handle--nw"
          @mousedown.prevent.stop="startResize('nw', $event)"
          @touchstart.prevent.stop="startResize('nw', $event)"
        ></div>
        <div
          class="resize-handle resize-handle--se"
          @mousedown.prevent.stop="startResize('se', $event)"
          @touchstart.prevent.stop="startResize('se', $event)"
        ></div>
        <div
          class="resize-handle resize-handle--sw"
          @mousedown.prevent.stop="startResize('sw', $event)"
          @touchstart.prevent.stop="startResize('sw', $event)"
        ></div>
      </template>
    </div>
  </div>
</template>

<script>
import { readOliverChat } from "@/components/OliverBotV2/utils/oliverLinks.js";
import OliverChatPanel from "@/components/OliverBotV2/OliverChatPanel.vue";
import Orb from "@/components/OliverBotV2/shared/Orb.vue";
import {
  buildThemeVars,
  getThemeOrbColors,
} from "@/components/OliverBotV2/theme/themes.js";
import {
  calculateCenteredPosition,
  calculatePopoverPosition,
  clampSize,
  createFullscreenStyles,
  createPositionStyles,
  getEffectiveMinSize,
  getResponsiveSize,
} from "@/components/OliverBotV2/utils/positionManager.js";
import draggable from "@/mixins/draggable.js";
import {
  loadFromStorage,
  removeFromStorage,
  saveToStorage,
} from "@/utils/storage.js";
import { mapState } from "vuex";

export default {
  name: "FloatingChatBotV2",
  components: {
    Orb,
    OliverChatPanel,
  },
  mixins: [draggable],
  data() {
    return {
      // UI State
      showChatBot: false,
      isFullscreen: false,
      isExitingFullscreen: false,
      isDocked: false,
      headerOffset: 0,

      // Store position before fullscreen for smooth transition back
      savedPosition: null,

      // Button drag state
      isDragging: false,
      hasDragged: false,
      dragOffset: { x: 0, y: 0, startX: 0, startY: 0 },
      buttonDimensions: { width: 0, height: 0 },
      position: {
        x:
          (window.innerWidth || document.documentElement.clientWidth || 0) -
          165,
        y: 60,
      },
      dragStartTime: 0,
      dragThreshold: 5,

      // Popover drag state
      isPopoverDragging: false,
      popoverHasDragged: false,
      popoverDragOffset: { x: 0, y: 0 },
      popoverPosition: { x: null, y: null },

      // Constants
      chatBotSize: { width: 450, height: 650 },
      DOCKED_WIDTH: 450,
      DOCKED_TOP_GAP: 2,
      MIN_MARGIN: 10,
      FULLSCREEN_MARGIN: 0,
      BUTTON_OFFSET: 80,
      MIN_POPOVER_WIDTH: 450,
      MIN_POPOVER_HEIGHT: 650,
      VIEWPORT_FILL_TOLERANCE: 4,
      // Quick-actions grid only fits without overlapping the sticky chat input
      // when the popover has BOTH enough width for 3 cards and enough height
      // for two card rows + the welcome content above + the input below.
      GRID_LAYOUT_MIN_WIDTH: 700,
      GRID_LAYOUT_MIN_HEIGHT: 800,

      // Reactive viewport size for popoverStyles
      viewportSize: {
        width: window.innerWidth,
        height: window.innerHeight,
      },

      // Prevent orb jump
      positionLoaded: false,

      // Resize state
      customSize: null,
      isResizing: false,
      resizeDirection: null,
      resizeStartX: 0,
      resizeStartY: 0,
      resizeStartSize: { width: 0, height: 0 },
      resizeStartPosition: { x: 0, y: 0 },

      // Snapshot of size/position before fullscreen — restored on exit
      preFullscreenSize: null,
      preFullscreenPosition: null,

      // Flag held during the reset CSS transition so .custom-sized stays applied
      // (its `max-width: none !important` override prevents the media-query cap
      // from snapping the popover instantly while the width animates).
      isResetting: false,

      // Tracks top navbar size changes to keep docked offset accurate.
      navbarResizeObserver: null,
    };
  },
  computed: {
    ...mapState({
      user: (state) => state.auth.user,
      deviceType: (state) => state.settings.deviceType,
      publicSettings: (state) => state.settings.publicSettings?.data,
      oliverTheme: (state) => state.oliverChatBotV2.theme,
      oliverDarkMode: (state) => state.oliverChatBotV2.darkMode,
      citationNavigations: (state) => state.oliverChatBotV2.citationNavigations,
    }),
    themeVars() {
      return buildThemeVars(this.oliverTheme, this.oliverDarkMode);
    },
    orbColors() {
      return getThemeOrbColors(this.oliverTheme);
    },
    isMobile: function () {
      return this.deviceType == "mobile";
    },
    isAllowed() {
      return this.user?.features?.assistant === true;
    },

    // Reset button is for SIZE only — drag-only repositioning does not surface it.
    isCustomized() {
      return !!this.customSize;
    },

    /**
     * Caps the chat input at 70% so it doesn't stretch when the popover is
     * significantly wider than the default. Uses the same width threshold as
     * the grid so the cap only fires after a meaningful resize, not on the
     * first 1px drag (which would cause an abrupt shrink). Height is irrelevant
     * here because the cap is purely horizontal.
     */
    isWideLayout() {
      return (
        this.isFullscreen ||
        (!!this.customSize &&
          this.customSize.width >= this.GRID_LAYOUT_MIN_WIDTH)
      );
    },

    /**
     * Switches the quick-actions slider to a 3-col grid only when the popover
     * has enough room in BOTH dimensions. Width alone isn't enough — a wide
     * but short popover can't fit two grid rows + the input without the input
     * overlapping the bottom row.
     */
    isGridLayout() {
      return (
        this.isFullscreen ||
        (!!this.customSize &&
          this.customSize.width >= this.GRID_LAYOUT_MIN_WIDTH &&
          this.customSize.height >= this.GRID_LAYOUT_MIN_HEIGHT)
      );
    },
    buttonStyles() {
      return {
        position: "fixed",
        right: `${this.position.x}px`,
        bottom: `${this.position.y}px`,
        zIndex: 1000,
        cursor: this.isDragging ? "grabbing" : "grab",
        userSelect: "none",
      };
    },
    popoverStyles() {
      const viewport = this.viewportSize;

      // Docked mode: right panel fixed below the horizontal navbar
      if (this.isDocked) {
        const dockedTop = this.headerOffset + this.DOCKED_TOP_GAP;
        return {
          position: "fixed",
          right: "0",
          top: `${dockedTop}px`,
          width: `${this.DOCKED_WIDTH}px`,
          height: `calc(100vh - ${dockedTop}px)`,
          maxWidth: "none",
          maxHeight: "none",
        };
      }

      // When exiting fullscreen, use saved position for smooth transition
      if (this.isExitingFullscreen && this.savedPosition) {
        return {
          ...this.savedPosition,
          transformOrigin: "bottom center",
        };
      }

      if (this.isFullscreen) {
        return createFullscreenStyles(viewport, this.FULLSCREEN_MARGIN);
      }

      // Use custom size when manually resized; otherwise derive from viewport/device
      const size = this.customSize
        ? { ...this.customSize }
        : getResponsiveSize(viewport, this.deviceType, this.chatBotSize);

      // Override CSS max-width/max-height when a custom size has been applied
      const overrideMaxStyles = this.customSize
        ? { maxWidth: "none", maxHeight: "none" }
        : {};

      // If popover has been manually positioned, use that position
      if (this.popoverPosition.x !== null && this.popoverPosition.y !== null) {
        return {
          ...createPositionStyles(
            { left: this.popoverPosition.x, top: this.popoverPosition.y },
            size
          ),
          ...overrideMaxStyles,
        };
      }

      // Calculate optimal position based on button position (default behavior)
      // On mobile, center the popover instead of positioning near button
      if (this.deviceType === "mobile") {
        const position = calculateCenteredPosition(viewport, size);
        return {
          ...createPositionStyles(position, size, "center center"),
          ...overrideMaxStyles,
        };
      }

      // Desktop: Calculate position based on button location
      const position = calculatePopoverPosition(
        this.position,
        viewport,
        size,
        this.MIN_MARGIN,
        this.BUTTON_OFFSET
      );

      return {
        ...createPositionStyles(position, size),
        ...overrideMaxStyles,
      };
    },
  },
  watch: {
    // Mobile uses responsive sizing only — clear any persisted custom size/position
    deviceType(newVal) {
      if (newVal === "mobile") {
        if (this.customSize) {
          this.customSize = null;
          removeFromStorage("chatBotV2CustomSize");
        }
        if (
          this.popoverPosition.x !== null ||
          this.popoverPosition.y !== null
        ) {
          this.popoverPosition = { x: null, y: null };
          removeFromStorage("chatBotV2PopoverPosition");
        }
        if (this.isDocked) {
          this.isDocked = false;
        }
      }
    },
    isDocked(val) {
      if (val) {
        saveToStorage("chatBotV2IsDocked", true);
        if (this.showChatBot) {
          this.refreshDockedLayout();
        }
      } else {
        removeFromStorage("chatBotV2IsDocked");
      }
      this.syncDockedBodyClasses();
    },
    isFullscreen(val) {
      // Watched rather than saved at the toggle: docking clears fullscreen directly, and a
      // save there would be missed, leaving a stale preference to resurrect on undock.
      if (val) {
        saveToStorage("chatBotV2IsFullscreen", true);
      } else {
        removeFromStorage("chatBotV2IsFullscreen");
      }
    },
    showChatBot(val) {
      if (this.isDocked && val) {
        this.refreshDockedLayout();
      }
      this.syncDockedBodyClasses();
    },
    $route(to) {
      this.setupNavbarObserver();
      if (this.isDocked && this.showChatBot) {
        this.refreshDockedLayout(8);
      }
      this.requestRestoreFromRoute(to);
    },
    // Close only, so fullscreen/size/position survive and reopening restores them.
    citationNavigations() {
      this.showChatBot = false;
    },
  },
  mounted() {
    this.setupEventListeners();
    this.loadPosition();
    this.loadCustomSize();
    this.loadPopoverPosition();
    this.$nextTick(() => {
      this.loadDockedState();
      this.setupNavbarObserver();
      this.syncDockedBodyClasses();
      if (this.isDocked && this.showChatBot) {
        this.refreshDockedLayout(8);
      }
      this.requestRestoreFromRoute();
    });
    window.addEventListener("resize", this.onWindowResize);
  },
  beforeDestroy() {
    this.removeEventListeners();
    this.teardownNavbarObserver();
    window.removeEventListener("resize", this.onWindowResize);
    document.documentElement.classList.remove("oliver-docked-html");
    document.body.classList.remove("oliver-docked");
    clearTimeout(this._dockResizeTimer);
    if (this._dockTransitionHandler) {
      document.body.removeEventListener(
        "transitionend",
        this._dockTransitionHandler
      );
    }
  },
  methods: {
    /**
     * Set up all event listeners for drag functionality
     */
    setupEventListeners() {
      const events = [
        { type: "mousemove", handler: this.onDrag },
        { type: "mouseup", handler: this.stopDrag },
        {
          type: "touchmove",
          handler: this.onDrag,
          options: { passive: false },
        },
        { type: "touchend", handler: this.stopDrag },
        { type: "mousemove", handler: this.onPopoverDrag },
        { type: "mouseup", handler: this.stopPopoverDrag },
        {
          type: "touchmove",
          handler: this.onPopoverDrag,
          options: { passive: false },
        },
        { type: "touchend", handler: this.stopPopoverDrag },
        { type: "mousemove", handler: this.onResize },
        { type: "mouseup", handler: this.stopResize },
        {
          type: "touchmove",
          handler: this.onResize,
          options: { passive: false },
        },
        { type: "touchend", handler: this.stopResize },
      ];

      events.forEach(({ type, handler, options }) => {
        document.addEventListener(type, handler, options);
      });
    },

    /**
     * Remove all event listeners
     */
    removeEventListeners() {
      const events = [
        { type: "mousemove", handler: this.onDrag },
        { type: "mouseup", handler: this.stopDrag },
        { type: "touchmove", handler: this.onDrag },
        { type: "touchend", handler: this.stopDrag },
        { type: "mousemove", handler: this.onPopoverDrag },
        { type: "mouseup", handler: this.stopPopoverDrag },
        { type: "touchmove", handler: this.onPopoverDrag },
        { type: "touchend", handler: this.stopPopoverDrag },
        { type: "mousemove", handler: this.onResize },
        { type: "mouseup", handler: this.stopResize },
        { type: "touchmove", handler: this.onResize },
        { type: "touchend", handler: this.stopResize },
      ];

      events.forEach(({ type, handler }) => {
        document.removeEventListener(type, handler);
      });
    },

    /**
     * Handle chat bot button click/toggle.
     * When docked, closing only hides the panel — the dock preference is
     * preserved so re-opening (via the Orb) restores the docked layout.
     */
    onChatBotClickedHandler() {
      if (this.isDocked) {
        this.showChatBot = !this.showChatBot;
        return;
      }
      if (!this.hasDragged) {
        this.showChatBot = !this.showChatBot;

        if (!this.showChatBot) {
          this.resetPopoverPosition();
        }
      }
    },

    // Also called from `mounted`: this component is gated on `features.assistant` and can
    // mount after the route has settled.
    requestRestoreFromRoute(route) {
      const conversationId = readOliverChat((route || this.$route)?.query);
      if (!conversationId) return;
      this.$store.dispatch("oliverChatBotV2/setRestoreRequest", {
        conversationId,
      });
    },

    /**
     * Reset popover position to default calculated position
     */
    resetPopoverPosition() {
      this.popoverPosition = { x: null, y: null };
      removeFromStorage("chatBotV2PopoverPosition");
    },

    /**
     * Start dragging the button
     */
    startDrag(event) {
      if (event.type === "mousedown") {
        event.preventDefault();
      }

      this.isDragging = true;
      this.hasDragged = false;
      this.dragStartTime = Date.now();

      const { clientX, clientY } = this.getEventCoordinates(event);
      const buttonRect = this.$refs.chatBotButton.getBoundingClientRect();

      // Store button dimensions at drag start
      this.buttonDimensions.width = buttonRect.width;
      this.buttonDimensions.height = buttonRect.height;

      // Calculate offset from top-left corner of button to mouse position
      this.dragOffset.x = clientX - buttonRect.left;
      this.dragOffset.y = clientY - buttonRect.top;
      this.dragOffset.startX = clientX;
      this.dragOffset.startY = clientY;
    },

    /**
     * Handle button dragging
     */
    onDrag(event) {
      if (!this.isDragging) return;

      const { clientX, clientY } = this.getEventCoordinates(event);

      // Check if we've moved enough to consider this a drag
      if (
        this.hasDragThresholdExceeded(
          this.dragOffset.startX,
          this.dragOffset.startY,
          clientX,
          clientY,
          this.dragThreshold
        )
      ) {
        event.preventDefault();
        this.hasDragged = true;

        const viewport = {
          width: window.innerWidth,
          height: window.innerHeight,
        };

        // Use stored button dimensions from drag start
        const buttonWidth = this.buttonDimensions.width;
        const buttonHeight = this.buttonDimensions.height;

        // Calculate new left and top positions based on mouse and drag offset
        const newLeft = clientX - this.dragOffset.x;
        const newTop = clientY - this.dragOffset.y;

        // Convert to right and bottom positions (since button uses right/bottom positioning)
        const newRight = viewport.width - newLeft - buttonWidth;
        const newBottom = viewport.height - newTop - buttonHeight;

        // Clamp to viewport bounds
        this.position.x = this.clamp(
          newRight,
          this.MIN_MARGIN,
          viewport.width - buttonWidth - this.MIN_MARGIN
        );
        this.position.y = this.clamp(
          newBottom,
          this.MIN_MARGIN,
          viewport.height - buttonHeight - this.MIN_MARGIN
        );
      }
    },

    /**
     * Stop dragging the button
     */
    stopDrag() {
      if (this.isDragging) {
        this.isDragging = false;
        this.savePosition();
        setTimeout(() => (this.hasDragged = false), 200);
      }
    },

    /**
     * Save button position to localStorage
     */
    savePosition() {
      saveToStorage("chatBotV2Position", this.position);
    },

    /**
     * Load button position from localStorage
     */
    loadPosition() {
      const saved = loadFromStorage("chatBotV2Position");
      if (saved) {
        this.position = saved;
      }
      this.positionLoaded = true;
    },

    /**
     * Handle window resize to keep button within bounds
     */
    onWindowResize() {
      this.viewportSize.width = window.innerWidth;
      this.viewportSize.height = window.innerHeight;
      if (this.isDocked && this.showChatBot) {
        this.refreshDockedLayout(8);
      }

      const viewport = this.viewportSize;

      // Always clamp button position
      this.position.x = this.clamp(
        this.position.x,
        this.MIN_MARGIN,
        viewport.width - 70
      );
      this.position.y = this.clamp(
        this.position.y,
        this.MIN_MARGIN,
        viewport.height - 70
      );
      this.savePosition();

      // Clamp custom size to the new viewport bounds (floor relaxes to viewport when needed)
      if (this.customSize) {
        this.customSize = clampSize(
          this.customSize,
          viewport,
          this.MIN_MARGIN,
          this.MIN_POPOVER_WIDTH,
          this.MIN_POPOVER_HEIGHT
        );
        this.saveCustomSize();
      }

      // If popover is manually positioned, clamp it to viewport to prevent overflow
      if (
        this.popoverPosition.x !== null &&
        this.popoverPosition.y !== null &&
        !this.isFullscreen
      ) {
        // Get popover size based on device type and viewport
        const size = getResponsiveSize(
          viewport,
          this.deviceType,
          this.chatBotSize
        );

        // Clamp popover position to ensure it stays within viewport bounds
        this.popoverPosition.x = this.clamp(
          this.popoverPosition.x,
          this.MIN_MARGIN,
          viewport.width - size.width - this.MIN_MARGIN
        );
        this.popoverPosition.y = this.clamp(
          this.popoverPosition.y,
          this.MIN_MARGIN,
          viewport.height - size.height - this.MIN_MARGIN
        );
        this.savePopoverPosition();
      }
    },

    /**
     * Toggle fullscreen mode.
     * On enter: snapshot the current custom size + popover position so exit can restore them.
     * On exit: restore the snapshot — but if the snapshot was effectively viewport-fill,
     * reset to default instead so the user gets visible "shrink" feedback (otherwise
     * fullscreen and the prior manual-max state are visually identical and the user
     * appears stuck in fullscreen).
     */
    toggleFullscreen() {
      if (!this.isFullscreen) {
        this.saveCurrentPosition();
        this.preFullscreenSize = this.customSize
          ? { ...this.customSize }
          : null;
        this.preFullscreenPosition =
          this.popoverPosition.x !== null && this.popoverPosition.y !== null
            ? { ...this.popoverPosition }
            : null;
        this.isFullscreen = true;
      } else {
        this.applyFullscreenExitState();
        this.isExitingFullscreen = true;
        this.isFullscreen = false;
        if (
          this.$refs.OLIVER_CHAT_PANEL &&
          this.$refs.OLIVER_CHAT_PANEL.historySidebarOpen
        ) {
          setTimeout(() => {
            this.$refs.OLIVER_CHAT_PANEL.historySidebarOpen = false;
          }, 300); // wait for the fullscreen transition to mostly finish so the sidebar close animation is smooth and doesn't jank the popover resize
        }

        setTimeout(() => {
          this.isExitingFullscreen = false;
          this.savedPosition = null;
          this.preFullscreenSize = null;
          this.preFullscreenPosition = null;
        }, 500);
      }
    },

    /**
     * Decide whether exiting fullscreen restores the prior custom size + position
     * or fully resets to default. Size and position are restored independently so
     * a drag-only customization (no resize) is preserved across fullscreen toggles.
     * If the prior size was effectively viewport-fill, restore would be a visual
     * no-op — reset both axes so the user clearly sees the popover shrink back.
     */
    applyFullscreenExitState() {
      const viewport = this.viewportSize;
      const tol = this.VIEWPORT_FILL_TOLERANCE;
      const maxW = viewport.width - this.MIN_MARGIN * 2;
      const maxH = viewport.height - this.MIN_MARGIN * 2;

      const wasViewportFill =
        this.preFullscreenSize &&
        this.preFullscreenSize.width >= maxW - tol &&
        this.preFullscreenSize.height >= maxH - tol;

      if (wasViewportFill) {
        this.customSize = null;
        removeFromStorage("chatBotV2CustomSize");
        this.popoverPosition = { x: null, y: null };
        removeFromStorage("chatBotV2PopoverPosition");
        return;
      }

      if (this.preFullscreenSize) {
        this.customSize = { ...this.preFullscreenSize };
        this.saveCustomSize();
      } else {
        this.customSize = null;
        removeFromStorage("chatBotV2CustomSize");
      }

      if (this.preFullscreenPosition) {
        this.popoverPosition = { ...this.preFullscreenPosition };
        this.savePopoverPosition();
      } else {
        this.popoverPosition = { x: null, y: null };
        removeFromStorage("chatBotV2PopoverPosition");
      }
    },

    /**
     * Save the current popover position before going fullscreen
     */
    saveCurrentPosition() {
      const viewport = {
        width: window.innerWidth,
        height: window.innerHeight,
      };

      const size = this.customSize
        ? { ...this.customSize }
        : getResponsiveSize(viewport, this.deviceType, this.chatBotSize);

      if (this.popoverPosition.x !== null && this.popoverPosition.y !== null) {
        // Use custom position
        this.savedPosition = createPositionStyles(
          { left: this.popoverPosition.x, top: this.popoverPosition.y },
          size
        );
      } else if (this.deviceType === "mobile") {
        // Mobile: center the popover
        const position = calculateCenteredPosition(viewport, size);
        this.savedPosition = createPositionStyles(
          position,
          size,
          "center center"
        );
      } else {
        // Desktop: position near button
        const position = calculatePopoverPosition(
          this.position,
          viewport,
          size,
          this.MIN_MARGIN,
          this.BUTTON_OFFSET
        );
        this.savedPosition = createPositionStyles(position, size);
      }
    },

    /**
     * Load popover position from localStorage and clamp to current viewport.
     * Required because the saved position may push the popover off-screen after a
     * viewport shrink or after the floor (MIN_POPOVER_WIDTH/HEIGHT) was raised.
     */
    loadPopoverPosition() {
      const saved = loadFromStorage("chatBotV2PopoverPosition", {
        x: null,
        y: null,
      });
      this.popoverPosition = { ...saved };

      if (saved.x === null || saved.y === null) return;

      const size =
        this.customSize ||
        getResponsiveSize(this.viewportSize, this.deviceType, this.chatBotSize);

      this.popoverPosition.x = this.clamp(
        saved.x,
        this.MIN_MARGIN,
        this.viewportSize.width - size.width - this.MIN_MARGIN
      );
      this.popoverPosition.y = this.clamp(
        saved.y,
        this.MIN_MARGIN,
        this.viewportSize.height - size.height - this.MIN_MARGIN
      );

      if (
        this.popoverPosition.x !== saved.x ||
        this.popoverPosition.y !== saved.y
      ) {
        this.savePopoverPosition();
      }
    },

    /**
     * Start dragging the popover window
     */
    startPopoverDrag(event) {
      if (this.isFullscreen) return;

      this.isPopoverDragging = true;
      this.popoverHasDragged = false;

      const { clientX, clientY } = this.getEventCoordinates(event);
      const popoverRect = this.$refs.chatBotPopover.getBoundingClientRect();

      // Calculate offset from top-left corner
      this.popoverDragOffset.x = clientX - popoverRect.left;
      this.popoverDragOffset.y = clientY - popoverRect.top;
    },

    /**
     * Handle popover dragging
     */
    onPopoverDrag(event) {
      if (!this.isPopoverDragging || this.isFullscreen) return;

      event.preventDefault();
      this.popoverHasDragged = true;

      const { clientX, clientY } = this.getEventCoordinates(event);
      const viewport = this.viewportSize;

      // Calculate new position
      let newX = clientX - this.popoverDragOffset.x;
      let newY = clientY - this.popoverDragOffset.y;

      // Get popover dimensions from current styles
      const popoverRect = this.$refs.chatBotPopover.getBoundingClientRect();
      const popoverWidth = popoverRect.width;
      const popoverHeight = popoverRect.height;

      // Clamp to viewport bounds
      newX = this.clamp(
        newX,
        this.MIN_MARGIN,
        viewport.width - popoverWidth - this.MIN_MARGIN
      );
      newY = this.clamp(
        newY,
        this.MIN_MARGIN,
        viewport.height - popoverHeight - this.MIN_MARGIN
      );

      // Update popover position
      this.popoverPosition.x = newX;
      this.popoverPosition.y = newY;
    },

    /**
     * Stop dragging the popover window
     */
    stopPopoverDrag() {
      if (this.isPopoverDragging) {
        this.isPopoverDragging = false;

        // Save position if actually dragged
        if (this.popoverHasDragged) {
          this.savePopoverPosition();
        }

        setTimeout(() => {
          this.popoverHasDragged = false;
        }, 200);
      }
    },

    /**
     * Save popover position to localStorage
     */
    savePopoverPosition() {
      saveToStorage("chatBotV2PopoverPosition", this.popoverPosition);
    },

    /**
     * Save custom popover size to localStorage
     */
    saveCustomSize() {
      if (this.customSize) {
        saveToStorage("chatBotV2CustomSize", this.customSize);
      }
    },

    /**
     * Load custom popover size from localStorage.
     * - On mobile, discard saved size (handles are disabled there).
     * - Otherwise, clamp legacy values into the current floor + viewport so the popover
     *   never renders below the new minimum or off-screen after a viewport shrink.
     */
    loadCustomSize() {
      const saved = loadFromStorage("chatBotV2CustomSize");
      if (!(saved && saved.width && saved.height)) return;

      if (this.deviceType === "mobile") {
        removeFromStorage("chatBotV2CustomSize");
        return;
      }

      this.customSize = clampSize(
        saved,
        this.viewportSize,
        this.MIN_MARGIN,
        this.MIN_POPOVER_WIDTH,
        this.MIN_POPOVER_HEIGHT
      );

      if (
        this.customSize.width !== saved.width ||
        this.customSize.height !== saved.height
      ) {
        this.saveCustomSize();
      }
    },

    /**
     * Begin a resize operation from one of the edge/corner handles.
     * Locks in the current rect so deltas are calculated from a stable baseline.
     * @param {string} direction - One of 'e' | 'w' | 's' | 'se' | 'sw'
     * @param {Event} event - Mouse or touch event
     */
    startResize(direction, event) {
      if (this.isFullscreen) return;

      this.isResizing = true;
      this.resizeDirection = direction;

      const { clientX, clientY } = this.getEventCoordinates(event);
      this.resizeStartX = clientX;
      this.resizeStartY = clientY;

      // Snapshot the popover's current rendered rect
      const rect = this.$refs.chatBotPopover.getBoundingClientRect();
      this.resizeStartSize = { width: rect.width, height: rect.height };
      this.resizeStartPosition = { x: rect.left, y: rect.top };

      // Pin popoverPosition so that position updates during resize work correctly
      this.popoverPosition = { x: rect.left, y: rect.top };

      // Set global cursor so it stays consistent even outside the handle
      const cursors = {
        n: "n-resize",
        s: "s-resize",
        e: "ew-resize",
        w: "ew-resize",
        ne: "ne-resize",
        nw: "nw-resize",
        se: "se-resize",
        sw: "sw-resize",
      };
      document.body.style.cursor = cursors[direction] || "default";
      document.body.style.userSelect = "none";
    },

    /**
     * Handle active resize mouse/touch move
     */
    onResize(event) {
      if (!this.isResizing) return;
      event.preventDefault();

      const { clientX, clientY } = this.getEventCoordinates(event);
      const dx = clientX - this.resizeStartX;
      const dy = clientY - this.resizeStartY;
      const viewport = this.viewportSize;
      const dir = this.resizeDirection;

      // Effective min relaxes when viewport is smaller than the preferred floor
      // (e.g., narrow tablets, browser-zoomed sessions).
      const minSize = getEffectiveMinSize(
        viewport,
        this.MIN_MARGIN,
        this.MIN_POPOVER_WIDTH,
        this.MIN_POPOVER_HEIGHT
      );

      let newWidth = this.resizeStartSize.width;
      let newHeight = this.resizeStartSize.height;
      let newLeft = this.resizeStartPosition.x;
      let newTop = this.resizeStartPosition.y;

      // North: top edge moves up → taller, top anchor shifts
      if (dir.includes("n")) {
        const maxHeightFromTop =
          this.resizeStartSize.height +
          this.resizeStartPosition.y -
          this.MIN_MARGIN;
        const proposedHeight = this.clamp(
          this.resizeStartSize.height - dy,
          minSize.height,
          maxHeightFromTop
        );
        newTop =
          this.resizeStartPosition.y +
          (this.resizeStartSize.height - proposedHeight);
        newHeight = proposedHeight;
      }

      // East: right edge moves right → wider
      if (dir.includes("e")) {
        newWidth = this.clamp(
          this.resizeStartSize.width + dx,
          minSize.width,
          viewport.width - newLeft - this.MIN_MARGIN
        );
      }

      // West: left edge moves left → wider and left-anchor shifts
      if (dir.includes("w")) {
        const maxWidthFromLeft =
          this.resizeStartSize.width +
          this.resizeStartPosition.x -
          this.MIN_MARGIN;
        const proposedWidth = this.clamp(
          this.resizeStartSize.width - dx,
          minSize.width,
          maxWidthFromLeft
        );
        newLeft =
          this.resizeStartPosition.x +
          (this.resizeStartSize.width - proposedWidth);
        newWidth = proposedWidth;
      }

      // South: bottom edge moves down → taller
      if (dir.includes("s")) {
        newHeight = this.clamp(
          this.resizeStartSize.height + dy,
          minSize.height,
          viewport.height - this.resizeStartPosition.y - this.MIN_MARGIN
        );
      }

      this.customSize = {
        width: Math.round(newWidth),
        height: Math.round(newHeight),
      };
      this.popoverPosition = {
        x: Math.round(newLeft),
        y: Math.round(newTop),
      };
    },

    /**
     * Finish resize and persist the new size + position.
     * If the resize landed exactly on the default size, drop customSize so the
     * popover behaves as "not customized" again (reset button hides, default
     * responsive sizing resumes on viewport changes).
     */
    stopResize() {
      if (!this.isResizing) return;
      this.isResizing = false;
      this.resizeDirection = null;
      document.body.style.cursor = "";
      document.body.style.userSelect = "";

      const isDefaultSize =
        this.customSize &&
        this.customSize.width === this.chatBotSize.width &&
        this.customSize.height === this.chatBotSize.height;

      if (isDefaultSize) {
        this.customSize = null;
        removeFromStorage("chatBotV2CustomSize");
      } else {
        this.saveCustomSize();
      }
      this.savePopoverPosition();
    },

    /**
     * Reset the popover to its default (responsive) size. Position is preserved.
     * Holds isResetting for the CSS transition duration so the .custom-sized
     * class stays attached, keeping `max-width: none !important` in effect while
     * the inline width/height animate down to default — without this, the
     * media-query `max-width: 450px !important` would snap the popover instantly.
     */
    resetSize() {
      if (!this.customSize) return;

      this.customSize = null;
      removeFromStorage("chatBotV2CustomSize");
      this.isResetting = true;

      setTimeout(() => {
        this.isResetting = false;
      }, 350);
    },

    /**
     * Load persisted dock and fullscreen preferences. Restores them across page loads
     * only on non-mobile devices. Sets classes synchronously to avoid a flash
     * of the undocked layout before Vue's watcher fires.
     */
    loadDockedState() {
      if (this.deviceType === "mobile") return;
      const saved = loadFromStorage("chatBotV2IsDocked", false);
      if (saved === true) {
        this.isDocked = true;
        this.showChatBot = false;
      }
      // Docked and fullscreen are separate layouts, so only one can apply.
      if (
        !this.isDocked &&
        loadFromStorage("chatBotV2IsFullscreen", false) === true
      ) {
        this.isFullscreen = true;
      }
    },

    /**
     * Keep global docked classes in sync with the visible docked state.
     * Layout should shift only when the panel is both docked and open.
     */
    syncDockedBodyClasses() {
      const shouldApplyDockedLayout = this.isDocked && this.showChatBot;
      document.documentElement.classList.toggle(
        "oliver-docked-html",
        shouldApplyDockedLayout
      );
      document.body.classList.toggle("oliver-docked", shouldApplyDockedLayout);
      // Fire a layout-settled signal the moment the padding-right transition
      // ends, so resize-aware components (FullCalendar, charts, etc.) can
      // recalculate at the correct final size without extra debounce delay.
      if (this._dockTransitionHandler) {
        document.body.removeEventListener(
          "transitionend",
          this._dockTransitionHandler
        );
      }
      clearTimeout(this._dockResizeTimer);
      const notify = () => window.dispatchEvent(new Event("resize"));
      this._dockTransitionHandler = (e) => {
        if (e.target === document.body && e.propertyName === "padding-right") {
          document.body.removeEventListener(
            "transitionend",
            this._dockTransitionHandler
          );
          this._dockTransitionHandler = null;
          notify();
        }
      };
      document.body.addEventListener(
        "transitionend",
        this._dockTransitionHandler
      );
      // Fallback in case transitionend never fires (prefers-reduced-motion, etc.)
      this._dockResizeTimer = setTimeout(() => {
        if (this._dockTransitionHandler) {
          document.body.removeEventListener(
            "transitionend",
            this._dockTransitionHandler
          );
          this._dockTransitionHandler = null;
          notify();
        }
      }, 350);
    },

    /**
     * Observe navbar height changes so docked top offset stays correct even
     * when nav content wraps/loads asynchronously on specific routes.
     */
    setupNavbarObserver(attempts = 6) {
      this.teardownNavbarObserver();

      const navbar = document.querySelector(
        ".navbar:not(.navbar-vertical):not(.navbar-expand-md.fixed-left)"
      );

      if (!navbar) {
        if (attempts > 0) {
          requestAnimationFrame(() => this.setupNavbarObserver(attempts - 1));
        }
        return;
      }

      if (typeof ResizeObserver !== "undefined") {
        this.navbarResizeObserver = new ResizeObserver(() => {
          if (this.isDocked && this.showChatBot) {
            this.refreshDockedLayout(2);
          }
        });
        this.navbarResizeObserver.observe(navbar);
      }
    },

    teardownNavbarObserver() {
      if (this.navbarResizeObserver) {
        this.navbarResizeObserver.disconnect();
        this.navbarResizeObserver = null;
      }
    },

    /**
     * Re-measure navbar offset after route/layout transitions. This avoids
     * occasional top clipping when navbar height settles asynchronously.
     */
    refreshDockedLayout(retries = 3) {
      if (!this.isDocked || !this.showChatBot) return;

      this.$nextTick(() => {
        const measured = this.measureHeaderOffset();
        if (!measured && retries > 0) {
          requestAnimationFrame(() => this.refreshDockedLayout(retries - 1));
        }
      });
    },

    /**
     * Measure the vertical distance from the viewport top to the bottom of the
     * horizontal navbar. Used to offset the docked panel so it starts just below
     * the navbar rather than overlapping it.
     */
    measureHeaderOffset() {
      const navbar = document.querySelector(
        ".navbar:not(.navbar-vertical):not(.navbar-expand-md.fixed-left)"
      );
      if (!navbar) {
        this.headerOffset = 0;
        return false;
      }

      this.headerOffset = Math.max(
        0,
        Math.ceil(navbar.getBoundingClientRect().bottom)
      );

      return this.headerOffset > 0;
    },

    /**
     * Toggle between docked (right-side panel) and floating modes.
     * Docking always shows the panel; undocking leaves it visible as floating.
     */
    toggleDock() {
      if (this.deviceType === "mobile") return;

      this.isDocked = !this.isDocked;

      if (this.isDocked) {
        this.showChatBot = true;
        if (this.isFullscreen) {
          this.isFullscreen = false;
        }
        this.customSize = null;
        removeFromStorage("chatBotV2CustomSize");
        this.popoverPosition = { x: null, y: null };
        removeFromStorage("chatBotV2PopoverPosition");
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.floating-chat-bot-v2 {
  z-index: 9999;
}
.floating-chat-bot-button {
  transition: all 0.2s ease-in-out;
  touch-action: none;

  &.dragging {
    transition: none;
    opacity: 0.8;
  }

  &:hover:not(.dragging) {
    transform: scale(1.05);
  }

  &:active {
    transform: scale(0.95);
  }
}

.chat-bot-popover {
  position: fixed;
  background: var(
    --oliver-ring-gradient,
    linear-gradient(135deg, #6ee7f9 0%, #8f8ae6 50%, #e5a3ff 100%)
  );
  border-radius: 16px;
  box-shadow: 0 20px 60px rgba(143, 138, 230, 0.3);
  border: 1px solid rgba(255, 255, 255, 0.3);
  overflow: hidden;
  opacity: 0;
  transform: scale(0.9) translateY(10px);
  transform-origin: center center;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  pointer-events: none;
  display: flex;
  flex-direction: column;
  visibility: hidden;

  &.show {
    opacity: 1;
    transform: none;
    pointer-events: all;
    visibility: visible;
  }

  &.dragging,
  &.resizing {
    transition: none;
    opacity: 0.9;
  }

  // When the user has manually resized, override the CSS max constraints
  // with a higher-specificity rule so they can't fight the inline width/height.
  &.custom-sized {
    max-width: none !important;
    max-height: none !important;
  }

  &.fullscreen {
    border-radius: 0;
    max-width: none !important;
    max-height: none !important;
  }

  &.exiting-fullscreen {
    border-radius: 12px;
    max-width: none !important;
    max-height: none !important;
  }

  &.docked {
    border-radius: 0;
    max-width: none !important;
    max-height: none !important;
    box-shadow: -6px 0 32px rgba(143, 138, 230, 0.18);
    border-left: 1px solid rgba(143, 138, 230, 0.25);
    z-index: 1000;

    &:not(.show) {
      transform: translateX(100%);
      opacity: 0;
    }

    &.show {
      transform: none;
      opacity: 1;
    }
  }

  // Resize handles — absolutely positioned inside the popover.
  // z-index must sit ABOVE the drawer overlay (QueueSidebar overlay root,
  // z-index 100) — the panel isn't its own stacking context, so that overlay
  // escapes to this popover level and would otherwise cover the edge/corner
  // handles, making the window un-resizable while the drawer is open.
  .resize-handle {
    position: absolute;
    z-index: 110;
    // Transparent by default; subtle on hover
    background: transparent;
    transition: background 0.15s ease;

    &:hover {
      background: rgba(255, 255, 255, 0.12);
    }

    // East: right edge — resizes width
    &--e {
      right: 0;
      top: 20px;
      bottom: 20px;
      width: 6px;
      cursor: ew-resize;
    }

    // West: left edge — resizes width + shifts left anchor
    &--w {
      left: 0;
      top: 20px;
      bottom: 20px;
      width: 6px;
      cursor: ew-resize;
    }

    // North: top edge — resizes height from top
    &--n {
      top: 0;
      left: 20px;
      right: 20px;
      height: 6px;
      cursor: n-resize;
    }

    // South: bottom edge — resizes height
    &--s {
      bottom: 0;
      left: 20px;
      right: 20px;
      height: 6px;
      cursor: s-resize;
    }

    // North-East corner
    &--ne {
      top: 2px;
      right: 2px;
      width: 16px;
      height: 16px;
      cursor: ne-resize;
      border-radius: 0 12px 0 0;
    }

    // North-West corner
    &--nw {
      top: 2px;
      left: 2px;
      width: 16px;
      height: 16px;
      cursor: nw-resize;
      border-radius: 12px 0 0 0;
    }

    // South-East corner — resize both dimensions, visible grip
    &--se {
      bottom: 2px;
      right: 2px;
      width: 18px;
      height: 18px;
      cursor: se-resize;
      border-radius: 0 0 12px 0;
      display: flex;
      align-items: flex-end;
      justify-content: flex-end;
      padding: 3px;

      // Dot-grid grip icon via CSS
      &::after {
        content: "";
        display: block;
        width: 8px;
        height: 8px;
        background-image: radial-gradient(
          circle,
          rgba(255, 255, 255, 0.6) 1.5px,
          transparent 1.5px
        );
        background-size: 4px 4px;
      }
    }

    // South-West corner
    &--sw {
      bottom: 2px;
      left: 2px;
      width: 16px;
      height: 16px;
      cursor: sw-resize;
      border-radius: 0 0 0 12px;
    }
  }
}

@media (max-width: 768px) {
  .chat-bot-popover {
    border-radius: 16px;
    max-width: calc(100vw - 20px) !important;
    max-height: calc(100vh - 80px) !important;
  }

  .floating-chat-bot-button {
    width: 70px;
    height: 70px;
  }
}

@media (min-width: 769px) and (max-width: 1024px) {
  .chat-bot-popover {
    max-width: 420px !important;
    max-height: calc(100vh - 120px) !important;
  }
}

@media (min-width: 1025px) {
  .chat-bot-popover {
    max-width: 450px !important;
    max-height: 700px !important;
  }
}

.floating-chat-bot-button,
.chat-bot-popover {
  z-index: 9999;
}
</style>

<style lang="scss">
html.oliver-docked-html {
  overflow-x: hidden;
}

body {
  transition: padding-right 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

body {
  &.oliver-docked {
    padding-right: 450px;

    @media (max-width: 768px) {
      padding-right: 0;
    }
    .chat-bot-popover.docked {
      z-index: 1001 !important;
    }
    .floating-menu {
      z-index: 1001 !important;
    }
    .conversation-list {
      right: calc(20px + 450px) !important;
      transition: right 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    }
    #gleap-frame-container,
    .gleap-frame-container,
    [id^="gleap-frame"] {
      right: 450px !important;
      transition: right 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    }
  }
}
</style>
